import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { CitationChip } from 'components/CitationChip';
import { CitationDetailsDialog } from 'components/CitationDetailsDialog';
import { FindingCitation, FindingEvent } from 'types/findings';
import { verdictFilterKey } from 'utils/findingFilter';
import './FindingCard.css';

export interface FindingCardProps {
  finding: FindingEvent;
  /**
   * Controlled collapsed state. When omitted, the card manages its own
   * collapse state via the header toggle. When provided, ValidationMode's
   * expand-all / collapse-all buttons drive every card in lockstep.
   */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean, finding: FindingEvent) => void;
  onCitationActivate?: (citation: FindingCitation, finding: FindingEvent) => void;
  /** Optional auth token forwarded to the US-266 citation asset API. */
  token?: string;
  /** Optional API host override forwarded to the US-266 citation asset API. */
  address?: string | null;
}

/**
 * Renders a single finding from the v2 SSE stream. When the finding has
 * citations, each is rendered as a keyboard-accessible chip that opens an
 * in-app source-detail dialog. When the finding has no citations, the
 * component falls back to the plain-text message.
 */
export const FindingCard: FunctionComponent<FindingCardProps> = ({
  finding,
  collapsed,
  onCollapsedChange,
  onCitationActivate,
  token,
  address = null,
}) => {
  const citations = finding.citations ?? [];
  const [activeCitation, setActiveCitation] = useState<FindingCitation | null>(null);
  const [citationDialogOpen, setCitationDialogOpen] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const citationTriggerRef = useRef<HTMLButtonElement | null>(null);
  const articleRef = useRef<HTMLElement | null>(null);
  const messageId = `finding-message-${finding.id}`;
  const ruleId = `finding-rule-${finding.id}`;
  const bodyId = `finding-body-${finding.id}`;

  const isControlled = typeof collapsed === 'boolean';
  const isCollapsed = isControlled ? Boolean(collapsed) : internalCollapsed;
  const filterKey = verdictFilterKey(finding.verdict);

  const handleCitationActivate = (citation: FindingCitation, trigger: HTMLButtonElement) => {
    citationTriggerRef.current = trigger;
    setActiveCitation(citation);
    setCitationDialogOpen(true);
    onCitationActivate?.(citation, finding);
  };

  const handleCitationDialogOpenChange = (open: boolean) => {
    setCitationDialogOpen(open);
    if (!open) {
      requestAnimationFrame(() => citationTriggerRef.current?.focus());
    }
  };

  const setCollapsed = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalCollapsed(next);
      }
      onCollapsedChange?.(next, finding);
    },
    [isControlled, onCollapsedChange, finding],
  );

  const handleToggleCollapse = () => setCollapsed(!isCollapsed);

  // Allow ValidationMode's search auto-expand to reach nested cards via the
  // shared `finding-card--collapsed` class contract without React re-renders.
  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    const observer = new MutationObserver(() => {
      if (isControlled) return;
      const domCollapsed = el.classList.contains('finding-card--collapsed');
      if (domCollapsed !== internalCollapsed) {
        setInternalCollapsed(domCollapsed);
      }
    });
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [isControlled, internalCollapsed]);

  return (
    <article
      ref={articleRef}
      className={[
        'finding-card',
        `finding-card--${finding.verdict?.toLowerCase() || 'unknown'}`,
        isCollapsed ? 'finding-card--collapsed' : '',
      ].filter(Boolean).join(' ')}
      data-testid="finding-card"
      data-finding-id={finding.id}
      data-rule-id={finding.rule_id}
      data-verdict={finding.verdict?.toLowerCase() || 'unknown'}
      data-filter-key={filterKey}
      aria-labelledby={messageId}
      aria-describedby={ruleId}
    >
      <header className="finding-card__header">
        <button
          type="button"
          className="finding-card__toggle"
          onClick={handleToggleCollapse}
          aria-expanded={!isCollapsed}
          aria-controls={bodyId}
          title={isCollapsed ? 'Expand finding' : 'Collapse finding'}
          aria-label={isCollapsed ? `Expand finding ${finding.rule_id}` : `Collapse finding ${finding.rule_id}`}
        >
          <span className="finding-card__chevron" aria-hidden="true">
            {isCollapsed ? '▸' : '▾'}
          </span>
        </button>
        <span id={ruleId} className="finding-card__rule">
          {finding.rule_id}
        </span>
        <span className={`finding-card__verdict finding-card__verdict--${finding.verdict?.toLowerCase() || 'unknown'}`}>
          {finding.verdict}
        </span>
      </header>
      <div id={bodyId} className="finding-card__body" hidden={isCollapsed}>
        <p id={messageId} className="finding-card__message">{finding.message}</p>
        {finding.verdict?.toLowerCase() === 'inconclusive' && finding.reason_code && (
          <p
            className="finding-card__reason-code"
            data-testid="finding-reason-code"
          >
            Reason: <code>{finding.reason_code}</code>
          </p>
        )}
        {citations.length > 0 && (
          <ul className="finding-card__citations" aria-label="Legislation citations">
            {citations.map((citation) => (
              <li key={citation.urn} className="finding-card__citation">
                <CitationChip
                  citation={citation}
                  onActivate={handleCitationActivate}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      <CitationDetailsDialog
        citation={activeCitation}
        open={citationDialogOpen}
        onOpenChange={handleCitationDialogOpenChange}
        token={token}
        address={address}
      />
    </article>
  );
};
